import { c as _c } from "react/compiler-runtime";
import { type useAgent } from '@copilotkit/react-core/v2';
import { type RefObject, useEffect, useState } from 'react';
import { listCopilotCsvFiles } from 'shared/api/copilot';
import { ApiClientError } from 'shared/api/fetch';
import { useGetCopilotChat } from 'shared/copilot/api';
import { type CopilotAgentId } from 'shared/copilot/copilotSurfaces';
import { type AttachedCsvFile } from 'shared/copilot/hooks/useCopilotCsvAttachment';
import { findLatestStandaloneAttachedCsvUri, resolveSessionRestoreFilename } from 'shared/copilot/utils/csvAttachment';
import { HttpStatusCodes } from 'shared/httpStatus';
import logger from 'shared/logger';
const log = logger().child({
  namespace: 'copilot/agentConversation'
});

/**
 * `unavailable`: the server says the chat is missing, foreign or malformed, so it will never load.
 * `transient`: a 5xx, network or other error; the chat may still exist and must not be replaced.
 */
export type CopilotChatHydrationFailureReason = 'unavailable' | 'transient';
const UNAVAILABLE_CHAT_STATUS_CODES: ReadonlySet<number> = new Set([HttpStatusCodes.BAD_REQUEST, HttpStatusCodes.FORBIDDEN, HttpStatusCodes.NOT_FOUND]);
export const classifyChatHydrationFailure = (error: unknown): CopilotChatHydrationFailureReason => error instanceof ApiClientError && UNAVAILABLE_CHAT_STATUS_CODES.has(error.status_code) ? 'unavailable' : 'transient';
type CopilotAgent = ReturnType<typeof useAgent>['agent'];
type RestoreSessionAttachment = (file: AttachedCsvFile | null, options?: {
  hasSelectedDatasource?: boolean;
}) => void;
interface ResolveHydratedCsvFilenameArgs {
  clientId: number;
  gcsUri: string;
  chatIdAtHydration: string;
  activeThreadIdRef: RefObject<string | null>;
  hasSelectedDatasourceRef: RefObject<boolean>;
  restoreSessionAttachment: RestoreSessionAttachment;
}
export async function resolveHydratedCsvFilename({
  clientId,
  gcsUri,
  chatIdAtHydration,
  activeThreadIdRef,
  hasSelectedDatasourceRef,
  restoreSessionAttachment
}: ResolveHydratedCsvFilenameArgs): Promise<void> {
  try {
    const files = await listCopilotCsvFiles(clientId);
    if (activeThreadIdRef.current !== chatIdAtHydration || hasSelectedDatasourceRef.current) {
      return;
    }
    const filenameByGcsUri = Object.fromEntries(files.map(file => [file.gcsUri, file.filename]));
    restoreSessionAttachment({
      gcsUri,
      filename: resolveSessionRestoreFilename(gcsUri, filenameByGcsUri)
    }, {
      hasSelectedDatasource: hasSelectedDatasourceRef.current
    });
  } catch (error) {
    log.warn({
      chatId: chatIdAtHydration,
      error: error instanceof Error ? error.message : String(error)
    }, 'failed to resolve restored csv attachment filename');
  }
}
interface UseCopilotChatHydrationArgs {
  agent: CopilotAgent;
  agentId: CopilotAgentId;
  clientId: number;
  userId: number | undefined;
  initialChatId: string | null;
  enableCsvAttachment: boolean;
  hasSelectedDatasource: boolean;
  activeThreadIdRef: RefObject<string | null>;
  hasSelectedDatasourceRef: RefObject<boolean>;
  restoreSessionAttachment: RestoreSessionAttachment;
  /**
   * Called when a load fails. Callers clear the session / URL (e.g. createNewChat) only for `unavailable`;
   * a `transient` failure keeps the chat, since it may still exist.
   */
  onHydrationFailed?: (chatId: string, reason: CopilotChatHydrationFailureReason) => void;
  /** Called when a load succeeds for the still-active thread, i.e. the server confirmed the chat exists. */
  onHydrated?: (chatId: string) => void;
}
export interface UseCopilotChatHydrationResult {
  hydrationTargetChatId: string | null;
  setHydrationTargetChatId: (chatId: string | null) => void;
  isHydrating: boolean;
}

/** Loads persisted chat messages on `loadChat` and restores standalone CSV session state. */
export const useCopilotChatHydration = t0 => {
  const $ = _c(20);
  const {
    agent,
    agentId,
    clientId,
    userId,
    initialChatId,
    enableCsvAttachment,
    hasSelectedDatasource,
    activeThreadIdRef,
    hasSelectedDatasourceRef,
    restoreSessionAttachment,
    onHydrationFailed,
    onHydrated
  } = t0;
  const [hydrationTargetChatId, setHydrationTargetChatId] = useState(initialChatId);
  const {
    data: hydratedChat,
    isFetching: isHydrating,
    isError: hydrationFailed,
    error: hydrationError
  } = useGetCopilotChat(clientId, hydrationTargetChatId, userId);
  let t1;
  let t2;
  if ($[0] !== activeThreadIdRef || $[1] !== agent || $[2] !== agentId || $[3] !== clientId || $[4] !== enableCsvAttachment || $[5] !== hasSelectedDatasource || $[6] !== hasSelectedDatasourceRef || $[7] !== hydratedChat || $[8] !== hydrationError || $[9] !== hydrationFailed || $[10] !== hydrationTargetChatId || $[11] !== isHydrating || $[12] !== onHydrated || $[13] !== onHydrationFailed || $[14] !== restoreSessionAttachment) {
    t1 = () => {
      if (hydrationTargetChatId == null || isHydrating) {
        return;
      }
      if (hydratedChat == null && hydrationFailed) {
        const failedChatId = hydrationTargetChatId;
        const reason = classifyChatHydrationFailure(hydrationError);
        log.warn({
          chatId: failedChatId,
          agentId,
          reason
        }, "failed to hydrate copilot chat messages");
        setHydrationTargetChatId(null);
        onHydrationFailed?.(failedChatId, reason);
        return;
      }
      if (hydratedChat == null) {
        return;
      }
      const chatIdAtHydration = hydrationTargetChatId;
      setHydrationTargetChatId(null);
      if (activeThreadIdRef.current !== chatIdAtHydration) {
        return;
      }
      onHydrated?.(chatIdAtHydration);
      const hydratedIds = new Set(hydratedChat.messages.map(_temp));
      const liveMessages = agent.messages.filter(message_0 => !hydratedIds.has(message_0.id));
      agent.setMessages([...hydratedChat.messages, ...liveMessages]);
      if (!enableCsvAttachment) {
        return;
      }
      const latestGcsUri = findLatestStandaloneAttachedCsvUri(hydratedChat.messages, {
        allowDocumentPartFallback: false
      });
      if (latestGcsUri == null || hasSelectedDatasource) {
        return;
      }
      restoreSessionAttachment({
        gcsUri: latestGcsUri,
        filename: resolveSessionRestoreFilename(latestGcsUri)
      }, {
        hasSelectedDatasource
      });
      resolveHydratedCsvFilename({
        clientId,
        gcsUri: latestGcsUri,
        chatIdAtHydration,
        activeThreadIdRef,
        hasSelectedDatasourceRef,
        restoreSessionAttachment
      });
    };
    t2 = [agent, agentId, clientId, enableCsvAttachment, hasSelectedDatasource, hasSelectedDatasourceRef, hydratedChat, hydrationError, hydrationFailed, hydrationTargetChatId, isHydrating, activeThreadIdRef, onHydrated, onHydrationFailed, restoreSessionAttachment];
    $[0] = activeThreadIdRef;
    $[1] = agent;
    $[2] = agentId;
    $[3] = clientId;
    $[4] = enableCsvAttachment;
    $[5] = hasSelectedDatasource;
    $[6] = hasSelectedDatasourceRef;
    $[7] = hydratedChat;
    $[8] = hydrationError;
    $[9] = hydrationFailed;
    $[10] = hydrationTargetChatId;
    $[11] = isHydrating;
    $[12] = onHydrated;
    $[13] = onHydrationFailed;
    $[14] = restoreSessionAttachment;
    $[15] = t1;
    $[16] = t2;
  } else {
    t1 = $[15];
    t2 = $[16];
  }
  useEffect(t1, t2);
  let t3;
  if ($[17] !== hydrationTargetChatId || $[18] !== isHydrating) {
    t3 = {
      hydrationTargetChatId,
      setHydrationTargetChatId,
      isHydrating
    };
    $[17] = hydrationTargetChatId;
    $[18] = isHydrating;
    $[19] = t3;
  } else {
    t3 = $[19];
  }
  return t3;
};
function _temp(message) {
  return message.id;
}